import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { Search, Shuffle, X } from "lucide-react";
import type { CoverArchive, CoverRelease, SimilarityArchive } from "./lib/types";
import { playCoverShift, primeCoverShiftAudio, type ShiftDirection } from "./lib/transitionSound";

type GridLayout = {
  name: "wide" | "narrow" | "portrait" | "phone";
  columns: number;
  query: [number, number];
  neighbours: [number, number][];
};

type GridCell = {
  index: number;
  column: number;
  row: number;
  query: boolean;
};

const fallbackDirections: ShiftDirection[] = [
  { x: 1, y: 0 },
  { x: -1, y: 0 },
  { x: 0, y: -1 },
];

const layouts: GridLayout[] = [
  {
    name: "phone",
    columns: 2,
    query: [1, 1],
    neighbours: [[1, 3], [2, 3], [1, 4], [2, 4], [1, 5], [2, 5]],
  },
  {
    name: "portrait",
    columns: 4,
    query: [2, 1],
    neighbours: [[1, 1], [1, 2], [4, 1], [4, 2], [2, 3], [3, 3], [1, 3], [4, 3], [1, 4], [2, 4], [3, 4], [4, 4]],
  },
  {
    name: "narrow",
    columns: 4,
    query: [2, 1],
    neighbours: [[1, 1], [1, 2], [4, 1], [4, 2], [2, 3], [3, 3], [1, 3], [4, 3]],
  },
  {
    name: "wide",
    columns: 6,
    query: [3, 1],
    neighbours: [[2, 1], [2, 2], [5, 1], [5, 2], [3, 3], [4, 3], [2, 3], [5, 3], [1, 1], [1, 2], [1, 3], [6, 1], [6, 2], [6, 3]],
  },
];

function layoutForViewport() {
  const ratio = window.innerWidth / window.innerHeight;
  if (ratio <= 3 / 5) return layouts[0];
  if (ratio <= 1) return layouts[1];
  if (ratio <= 5 / 3) return layouts[2];
  return layouts[3];
}

function useGridLayout() {
  const [layout, setLayout] = useState(layoutForViewport);
  useEffect(() => {
    const update = () => setLayout(layoutForViewport());
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return layout;
}

function normalize(value: string) {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function yearLabel(release: CoverRelease) {
  return release.year ? String(release.year) : "Undated";
}

function directionFromCell(cell: GridCell, layout: GridLayout): ShiftDirection {
  const queryLeft = layout.query[0];
  const queryTop = layout.query[1];
  const queryRight = queryLeft + 2;
  const queryBottom = queryTop + 2;

  if (cell.row >= queryBottom) return { x: 0, y: -1 };
  if (cell.row + 1 <= queryTop) return { x: 0, y: 1 };
  if (cell.column + 1 <= queryLeft) return { x: 1, y: 0 };
  if (cell.column >= queryRight) return { x: -1, y: 0 };
  return { x: 0, y: -1 };
}

function returnPhoneToTop(reducedMotion: boolean) {
  const from = window.scrollY;
  if (from <= 0) return;
  if (reducedMotion) {
    window.scrollTo(0, 0);
    return;
  }

  const startedAt = performance.now();
  const duration = 680;
  const step = (now: number) => {
    const progress = Math.min(1, (now - startedAt) / duration);
    const eased = progress < 0.5
      ? 4 * progress * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 3) / 2;
    window.scrollTo(0, from * (1 - eased));
    if (progress < 1) window.requestAnimationFrame(step);
  };
  window.requestAnimationFrame(step);
}

function CoverCell({
  cell,
  release,
  palette,
  swatch,
  onNavigate,
}: {
  cell: GridCell;
  release: CoverRelease;
  palette: string[];
  swatch: string;
  onNavigate: (cell: GridCell) => void;
}) {
  const style = {
    "--cell-column": cell.column,
    "--cell-row": cell.row,
    "--cell-swatch": swatch,
    "--cell-delay": `${cell.query ? 0 : (cell.column * 37 + cell.row * 61) % 90}ms`,
  } as React.CSSProperties;
  const content = (
    <>
      <span className="cover-frame">
        <img src={release.cover} alt="" draggable="false" />
      </span>
      <span className="cover-label">
        <strong>{release.title}</strong>
        <span className="cover-meta">
          <span>{release.artist}, {yearLabel(release)}</span>
          {cell.query && (
            <span className="cover-palette" aria-label="Extracted cover palette">
              {palette.map((colour, index) => <i key={`${colour}-${index}`} style={{ backgroundColor: colour }} />)}
            </span>
          )}
        </span>
      </span>
    </>
  );

  if (cell.query) {
    return <article className="cover-cell query" style={style}>{content}</article>;
  }
  return (
    <button
      className="cover-cell neighbour"
      style={style}
      type="button"
      aria-label={`${release.title}, ${release.artist}, ${yearLabel(release)}`}
      onClick={() => onNavigate(cell)}
    >
      {content}
    </button>
  );
}

/* Info panel is intentionally hidden for now.
function AboutPanel({ archive, similarity, onClose }: {
  archive: CoverArchive;
  similarity: SimilarityArchive;
  onClose: () => void;
}) {
  return (
    <>
      <button className="about-backdrop" type="button" aria-label="Close info" onClick={onClose} />
      <aside className="about-panel" aria-label="About plaka">
        <button className="about-close" type="button" aria-label="Close info" onClick={onClose}>
          <X size={18} aria-hidden="true" />
        </button>
        <p className="about-version">plaka. / {archive.total.toLocaleString()} covers</p>
        <p>Filipino album covers, arranged by visual resemblance.</p>
        <p>
          A cover leads to others that share its colours, graphic rhythm, photographic language, or composition.
          The result is a path through Filipino recorded music that can jump decades without losing the visual thread.
        </p>
        <p>
          No single metric reads a cover well. plaka combines six lenses: dominant palette, colour distribution,
          spatial colour, DINOv2, CSD, and DreamSim. The browser receives a precomputed neighbour graph, so each
          move is immediate.
        </p>
        <p>
          The current catalog runs from {archive.earliestYear} to {archive.latestYear}. Metadata comes from
          MusicBrainz and Discogs; artwork comes from the Cover Art Archive, Internet Archive, and matched Discogs
          records. Every image remains the property of its respective rights holder.
        </p>
        <p className="about-method">
          Similarity graph generated {new Date(similarity.generatedAt).toLocaleDateString("en-PH", { dateStyle: "long" })}.
          Early shellac, private pressings, regional releases, and undocumented recordings remain underrepresented.
        </p>
        <p className="about-credit">A Hapinas archive.</p>
      </aside>
    </>
  );
}
*/

export default function App() {
  const layout = useGridLayout();
  const searchRef = useRef<HTMLInputElement>(null);
  const timers = useRef<number[]>([]);
  const [archive, setArchive] = useState<CoverArchive | null>(null);
  const [similarity, setSimilarity] = useState<SimilarityArchive | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState<"idle" | "out" | "in">("idle");
  const [direction, setDirection] = useState<ShiftDirection>({ x: 1, y: 0 });
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const [searchActive, setSearchActive] = useState(false);
  // const [aboutOpen, setAboutOpen] = useState(false);

  useEffect(() => {
    const unlockAudio = () => primeCoverShiftAudio();
    const options = { capture: true, once: true };
    window.addEventListener("pointerdown", unlockAudio, options);
    window.addEventListener("keydown", unlockAudio, options);
    return () => {
      window.removeEventListener("pointerdown", unlockAudio, true);
      window.removeEventListener("keydown", unlockAudio, true);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch("/data/releases.json").then((response) => {
        if (!response.ok) throw new Error(`Catalog request failed (${response.status})`);
        return response.json() as Promise<CoverArchive>;
      }),
      fetch("/data/similarity.json").then((response) => {
        if (!response.ok) throw new Error(`Similarity request failed (${response.status})`);
        return response.json() as Promise<SimilarityArchive>;
      }),
    ]).then(([catalog, graph]) => {
      if (cancelled) return;
      if (catalog.releases.length !== graph.ids.length) throw new Error("The catalog and similarity graph do not match.");
      setArchive(catalog);
      setSimilarity(graph);
      const requested = new URLSearchParams(window.location.search).get("album");
      const requestedIndex = requested ? graph.ids.indexOf(requested) : -1;
      setCurrentIndex(requestedIndex >= 0 ? requestedIndex : Math.floor(Math.random() * catalog.releases.length));
    }).catch((reason: unknown) => {
      if (!cancelled) setError(reason instanceof Error ? reason.message : "The archive could not be opened.");
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => () => timers.current.forEach(window.clearTimeout), []);

  const results = useMemo(() => {
    if (!archive || deferredQuery.trim().length < 2) return [];
    const words = normalize(deferredQuery).trim().split(/\s+/).filter(Boolean);
    return archive.releases
      .map((release, index) => ({ release, index }))
      .filter(({ release }) => {
        const text = normalize(`${release.artist} ${release.title} ${release.year ?? ""}`);
        return words.every((word) => text.includes(word));
      })
      .sort((left, right) => (right.release.year ?? 0) - (left.release.year ?? 0))
      .slice(0, 20);
  }, [archive, deferredQuery]);

  const cells = useMemo(() => {
    if (!similarity) return [];
    const neighbours = similarity.neighbours[currentIndex] ?? [];
    const next: GridCell[] = [{ index: currentIndex, column: layout.query[0], row: layout.query[1], query: true }];
    layout.neighbours.forEach(([column, row], rank) => {
      const index = neighbours[rank];
      if (index !== undefined) next.push({ index, column, row, query: false });
    });
    return next;
  }, [currentIndex, layout, similarity]);

  useEffect(() => {
    if (!archive || !similarity) return;
    const candidates = [currentIndex, ...(similarity.neighbours[currentIndex] ?? []).slice(0, 18)];
    for (const index of candidates) {
      const image = new Image();
      image.src = archive.releases[index].cover;
    }
  }, [archive, currentIndex, similarity]);

  const setUrl = useCallback((index: number, graph: SimilarityArchive) => {
    const url = new URL(window.location.href);
    url.searchParams.set("album", graph.ids[index]);
    window.history.replaceState(null, "", url);
  }, []);

  const navigate = useCallback((index: number, cell?: GridCell) => {
    if (!similarity || phase !== "idle" || index === currentIndex) return;
    const selectedDirection = cell
      ? directionFromCell(cell, layout)
      : fallbackDirections[Math.floor(Math.random() * fallbackDirections.length)];
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (layout.name === "phone") returnPhoneToTop(reducedMotion);
    setDirection(selectedDirection);
    void playCoverShift(selectedDirection);
    setPhase("out");
    timers.current.push(window.setTimeout(() => {
      setCurrentIndex(index);
      setUrl(index, similarity);
      setPhase("in");
    }, 320));
    timers.current.push(window.setTimeout(() => setPhase("idle"), 800));
    setQuery("");
    setSearchActive(false);
  }, [currentIndex, layout, phase, setUrl, similarity]);

  useEffect(() => {
    const onPopState = () => {
      if (!similarity) return;
      const requested = new URLSearchParams(window.location.search).get("album");
      const index = requested ? similarity.ids.indexOf(requested) : -1;
      if (index >= 0) setCurrentIndex(index);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        // setAboutOpen(false);
        setSearchActive(false);
        setQuery("");
      }
      if (event.key === "/" && !searchActive) {
        event.preventDefault();
        setSearchActive(true);
        window.setTimeout(() => searchRef.current?.focus(), 0);
      }
      if (phase === "idle" && similarity && (event.key === "ArrowRight" || event.key === "ArrowDown")) {
        const next = similarity.neighbours[currentIndex]?.[0];
        if (next !== undefined) navigate(next);
      }
    };
    window.addEventListener("popstate", onPopState);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [currentIndex, navigate, phase, searchActive, similarity]);

  if (error) return <main className="state-screen"><strong>plaka could not open.</strong><span>{error}</span></main>;
  if (!archive || !similarity) return <main className="state-screen"><strong>plaka.</strong><span>Reading the covers</span></main>;

  const rootStyle = {
    "--travel-x": `${direction.x * 100}%`,
    "--travel-y": `${direction.y * 100}%`,
    "--entry-x": `${direction.x * -100}%`,
    "--entry-y": `${direction.y * -100}%`,
    "--label-travel-x": `${direction.x * 10}px`,
    "--label-travel-y": `${direction.y * 10}px`,
    "--label-entry-x": `${direction.x * -10}px`,
    "--label-entry-y": `${direction.y * -10}px`,
  } as React.CSSProperties;

  return (
    <main className={`archive-app layout-${layout.name} phase-${phase}`} style={rootStyle}>
      <header className="archive-header">
        <div className="archive-topbar">
          <button
            className="text-control shuffle-control"
            type="button"
            onClick={() => navigate(Math.floor(Math.random() * archive.releases.length))}
          >
            <Shuffle size={16} aria-hidden="true" /> shuffle
          </button>

          <div className={`archive-search ${searchActive ? "active" : ""}`}>
            <button
              className="mobile-search-toggle"
              type="button"
              onClick={() => {
                setSearchActive(true);
                window.setTimeout(() => searchRef.current?.focus(), 0);
              }}
            >
              <Search size={16} aria-hidden="true" /> search
            </button>
            <Search className="search-icon" size={17} aria-hidden="true" />
            <input
              ref={searchRef}
              value={query}
              type="search"
              placeholder={`search ${archive.total.toLocaleString()} Filipino covers`}
              aria-label="Search Filipino album covers"
              autoComplete="off"
              onFocus={() => setSearchActive(true)}
              onChange={(event) => setQuery(event.target.value)}
            />
            {searchActive && (
              <button className="search-close" type="button" aria-label="Close search" onClick={() => {
                setQuery("");
                setSearchActive(false);
              }}>
                <X size={16} aria-hidden="true" />
              </button>
            )}
            {query.trim().length >= 2 && (
              <div className="search-results" role="listbox">
                {results.length > 0 ? results.map(({ release, index }) => (
                  <button key={release.id} type="button" onClick={() => navigate(index)}>
                    <strong>{release.title}</strong>
                    <span>{release.artist}{release.year ? `, ${release.year}` : ""}</span>
                  </button>
                )) : <p>No matching cover</p>}
              </div>
            )}
          </div>

          {/* Info is intentionally hidden for now.
          <button className="text-control info-control" type="button" aria-expanded={aboutOpen} onClick={() => setAboutOpen(true)}>
            <Info size={15} aria-hidden="true" /> info
          </button>
          */}
        </div>
      </header>

      <section className="similarity-grid" aria-label="Visually related Filipino album covers">
        {cells.map((cell) => {
          const release = archive.releases[cell.index];
          return (
            <CoverCell
              key={`${cell.column}-${cell.row}`}
              cell={cell}
              release={release}
              palette={similarity.palettes[cell.index] ?? release.palette}
              swatch={similarity.swatches[cell.index] ?? release.palette[0]}
              onNavigate={(selected) => navigate(selected.index, selected)}
            />
          );
        })}
      </section>

      <footer className="archive-footer">
        <strong>plaka.</strong>
        <span>Filipino album covers by visual resemblance</span>
      </footer>

      {/* {aboutOpen && <AboutPanel archive={archive} similarity={similarity} onClose={() => setAboutOpen(false)} />} */}
    </main>
  );
}
